/* global document, localStorage, Office, module, require, HTMLElement */
import * as React from "react";
import { createRoot } from "react-dom/client";
import { FluentProvider, webLightTheme, webDarkTheme } from "@fluentui/react-components";
import App from "./components/App";
import PlaceholderTag from "./interfaces/PlaceholderTag";

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;
const darkTheme = Office.context.officeTheme.isDarkTheme;

const RESIZE_OBSERVER_NOISE = [
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
];

export enum LocalStorageKey {
  Tags = "placeholderTags",
  AccessToken = "accessToken",
  SessionId = "sessionId",
}

export type LocalStorageValue = {
  [LocalStorageKey.Tags]: PlaceholderTag[];
  [LocalStorageKey.AccessToken]: string;
  [LocalStorageKey.SessionId]: string;
};

export function getLocalStorage<K extends keyof LocalStorageValue>(key: K): LocalStorageValue[K] | null {
  const item = localStorage.getItem(key);
  return item ? JSON.parse(item) : null;
}

export function setLocalStorage<K extends keyof LocalStorageValue>(key: K, value: LocalStorageValue[K]): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function removeLocalStorage(key: LocalStorageKey): void {
  localStorage.removeItem(key);
}

function isResizeObserverNoise(reason: unknown): boolean {
  if (typeof reason === "string") {
    return RESIZE_OBSERVER_NOISE.some((text) => reason.includes(text));
  }
  if (reason && typeof reason === "object") {
    const maybeMessage = (reason as { message?: unknown }).message;
    if (typeof maybeMessage === "string") {
      return RESIZE_OBSERVER_NOISE.some((text) => maybeMessage.includes(text));
    }
  }
  return false;
}

if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    const message = event.message || (event.error as { message?: string } | undefined)?.message;
    if (!isResizeObserverNoise(message)) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (!isResizeObserverNoise(event.reason)) {
      return;
    }
    event.preventDefault();
  });
}

Office.onReady(async () => {
  root?.render(
    <FluentProvider theme={darkTheme ? webDarkTheme : webLightTheme}>
      <App />
    </FluentProvider>
  );
});

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(NextApp);
  });
}
